import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, provide, reactive, Ref, ref, watch } from 'vue';
import ElementLibrarySelector from '@/components/DesignStudio/ElementLibrarySelector.vue';
import CInput from '@/components/shared/CInput';
import CCheckbox from '@/components/shared/CCheckbox.vue';
import CButton from '@/components/shared/CButton';
import CSelect, { valueReducer, type CSelectItem } from '@/components/shared/CSelect';
import { ptToPx, pxToPt } from '@/utils/elementsUtils';
import fontManagerService from '@/utils/fontManagerService';
import { LibraryTypeEnum } from '@/utils/enums/libraryType.enum';
import type { ActiveSelection, BaseCanvasElement, BaseTextElement, BoundedTextElement, PathITextElement, TextTransformElement } from '@/typings/customilyElements/canvasElements';
import type { CanvasEx } from '@/typings/canvas';
import type { KeyValueDto } from '@/typings/keyValueDto';
import type { ElementMapOption, TextCharacterProvider } from './types';
import type { LibraryDetailElement } from '@/typings/libraries';
import { runTasksInChunks } from '@/utils/promiseUtils';
import { getLibraryElementDetails } from '@/api/libraries';
import { useTextShapeState } from '@/composables/useTextShapeState';


export default /*#__PURE__*/_defineComponent({
  __name: 'TextCharacterProperties',
  props: {
    canvas: null,
    textElement: null,
    forTypes: null,
    editMode: { type: Boolean },
    selectedGroup: null
  },
  emits: ["open-font-library-selector", "trigger-loading", "element-props-updated"],
  setup(__props: any, { emit }: { emit: ({
    (e: 'open-font-library-selector'): void;
    (e: 'trigger-loading', isLoading: boolean): void;
    (e: 'element-props-updated', value: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    canvas: CanvasEx;
    textElement?: BoundedTextElement | TextTransformElement | PathITextElement;
    forTypes?: string[];
    editMode?: boolean;
    selectedGroup?: ActiveSelection<BaseTextElement>
};

const FONT_TYPE = LibraryTypeEnum.Font;

const textSideOptions: CSelectItem<boolean>[] = [
    {
        label: 'Concave',
        value: false
    },
    {
        label: 'Convex',
        value: true
    }
];

const verticalAlignOptions: CSelectItem[] = [
    {
        label: 'Top',
        value: 'top'
    },
    {
        label: 'Center',
        value: 'center'
    },
    {
        label: 'Bottom',
        value: 'bottom'
    },
];

const horizontalAlignOptions = computed<CSelectItem[]>(() => ([
    {
        label: 'Left',
        value: 'left'
    },
    {
        label: 'Right',
        value: 'right'
    },
    {
        label: 'Center',
        value: 'center'
    },
    {
        label: 'Justify',
        value: 'justify',
        disabled: (model.tracking || 0) > 0
    }
]));





const type = ref({
    text: true,
    warpText: false,
    circularText: false
});

const lastEdited: Ref<'minSizePx' | 'maxSizePx' | null> = ref(null);

const model = reactive({
    caps: false,
    multiline: false,
    verticalText: false,
    ignoreLineBreaks: false,
    textVerticalAlign: '',
    textAlign: '',
    minSize: 0 as number | undefined,
    maxSize: 0 as number | undefined,
    tracking: 0 as number | undefined,
    lineSpacing: 0 as number | undefined,
    fontMap: new Map<number, string>(),
    fontLibraryId: undefined as number | undefined,
    fontOption: 1,
    circularConvex: false as boolean | undefined,
    circularStartAngle: 0 as number | undefined,
    circularEndAngle: 0 as number | undefined,
    isFontMapMixed: false,
    isFontLibraryIdMixed: false,
    placeholder: ''
});

const disabled = computed(() => {
    const element= props.textElement;
    let state = { common: false, tracking: false, align: false};

    if (!element) return state;

    const locked = element.locked;
    state = { common: locked, tracking: locked, align: locked};

    if (type.value.circularText) {
        state.tracking ||= model.textAlign == horizontalAlignOptions.value.at(-1)?.value;
    }

    if(type.value.text) {
        state.align ||= element.allowEdit;
    }

    return state;
});

const canvasDpi = computed(() => props.canvas.DPI);
const isBoundedText = computed(() => props.textElement?.type === 'bounded-text');
const hasTextPathDraw = computed(
    () => isBoundedText.value && !!(props.textElement as BoundedTextElement)?.textPath
);
// Shapes are set from the Shape panel and force multiline
const { hasTextShape } = useTextShapeState(
    () => props.textElement,
    (element) => {
        model.multiline = element.multiline;
        model.verticalText = element.verticalText;
        model.textAlign = element.textAlign;
        model.textVerticalAlign = element.textVerticalAlign;
        model.ignoreLineBreaks = !!element.shapeConfig?.ignoreLineBreaks;
    }
);

const initModel = (element: BoundedTextElement | TextTransformElement | PathITextElement) => {
    type.value = {
        text: false,
        circularText: false,
        warpText: false,
    };

    model.isFontMapMixed = false;
    model.isFontLibraryIdMixed = false;
    model.caps = element.caps;
    model.fontMap = new Map(element.fontsMap);
    model.fontLibraryId = element.fontLibraryId;
    model.fontOption = element.fontOption;

    if (element.type == 'text-transform') {
        type.value.warpText = true
        return;
    }

    model.textAlign = element.textAlign;
    model.minSize = pxToPt(element.minSizePx, canvasDpi.value);
    model.maxSize = pxToPt(element.maxSizePx, canvasDpi.value);
    model.tracking = element.tracking;

    switch (element.type) {
        case 'bounded-text':
            type.value.text = true;
            model.lineSpacing = element.lineSpacing * 100;
            model.multiline = element.multiline;
            model.verticalText = element.verticalText;
            model.textVerticalAlign = element.textVerticalAlign;
            model.ignoreLineBreaks = !!element.shapeConfig?.ignoreLineBreaks;
            break;
        case 'path-itext':
            type.value.circularText = true;
            model.circularStartAngle = element.startAngle;
            model.circularEndAngle = element.endAngle;
            model.circularConvex = element.convex;
            break;
    }

};

watch(() => props.textElement, (element) => {
    if (!element || (props.forTypes?.length && !props.forTypes.includes(element.type))) return;

    initModel(element);
}, { immediate: true });

const isFontMapMixed = (
    firstMap: BaseTextElement['fontsMap'],
    secondMap: BaseTextElement['fontsMap']
): boolean => {
    if (firstMap.size !== secondMap.size) return true;
    return [...firstMap.entries()].some(([key, value]) => {
        const mapValue = secondMap.get(key);
        return mapValue !== value;
    });
};

watch(
    () => props.selectedGroup,
    (group) => {
        const objects = group?.getObjects() as (BoundedTextElement | PathITextElement)[];
        if (!objects?.length) return;

        model.placeholder = 'Mixed';
        const firstElement = objects[0];
        initModel(firstElement);
        model.fontMap = new Map();

        const isCircular = type.value.circularText;
        const isWarp = type.value.warpText;

        // initModel converted these to pt/%; compare in raw units and convert back after the loop
        if (!isWarp) {
            model.minSize = firstElement.minSizePx;
            model.maxSize = firstElement.maxSizePx;
        }
        if (!isCircular && !isWarp) model.lineSpacing = (firstElement as BoundedTextElement).lineSpacing;

        for (const element of objects) {
            if (!model.isFontMapMixed) {
                model.isFontMapMixed = isFontMapMixed(firstElement.fontsMap, element.fontsMap)
            }

            if (!model.isFontLibraryIdMixed && element.fontLibraryId !== firstElement.fontLibraryId) {
                model.isFontLibraryIdMixed = true;
                model.fontLibraryId = undefined;
            }

            if (model.caps != element.caps) model.caps = false;

            // Warped text exposes only the font and All Caps; initModel leaves the rest untouched
            if (isWarp) continue;

            if (model.minSize != element.minSizePx) model.minSize = undefined;
            if (model.maxSize != element.maxSizePx) model.maxSize = undefined;
            if (model.textAlign != element.textAlign) model.textAlign = '';
            if (model.tracking != element.tracking) model.tracking = undefined;

            if (isCircular) {
                const circular = element as PathITextElement;
                if (model.circularStartAngle != circular.startAngle) model.circularStartAngle = undefined;
                if (model.circularEndAngle != circular.endAngle) model.circularEndAngle = undefined;
                if (model.circularConvex != circular.convex) model.circularConvex = undefined;
                continue;
            }

            const bounded = element as BoundedTextElement;
            if (model.textVerticalAlign != bounded.textVerticalAlign) model.textVerticalAlign = '';
            if (model.lineSpacing != bounded.lineSpacing) model.lineSpacing = undefined;
            if (model.multiline != bounded.multiline) model.multiline = false;
            if (model.verticalText != bounded.verticalText) model.verticalText = false;
            if (model.ignoreLineBreaks != !!bounded.shapeConfig?.ignoreLineBreaks) model.ignoreLineBreaks = false;
        }

        if (!isWarp && model.minSize != undefined) model.minSize = pxToPt(firstElement.minSizePx, canvasDpi.value);
        if (!isWarp && model.maxSize != undefined) model.maxSize = pxToPt(firstElement.maxSizePx, canvasDpi.value);
        if (!isCircular && !isWarp && model.lineSpacing != undefined) {
            model.lineSpacing = (firstElement as BoundedTextElement).lineSpacing * 100;
        }
        if (!model.isFontMapMixed) model.fontMap = new Map(firstElement.fontsMap);
    },
    { immediate: true }
);

const updateElement = async (
    callback: (element: BaseTextElement, list?: BaseTextElement[]) => void | Promise<void>
) => {
    const promises: Promise<void>[] = [];
    const objects = props.selectedGroup ? props.selectedGroup.getObjects() : [props.textElement as BaseTextElement];

    for (const element of objects) {
        if (!element) continue;
        promises.push(Promise.resolve(callback(element, objects)));
    }
    await runTasksInChunks(promises);
};

const updateElementProp = (key: string, value: unknown, enableEdit = true) => {
    updateElement((element) => {
        if (props.editMode && enableEdit) {
            element.setEditedProp(key);
        }

        if (props.editMode && ['maxSizePx', 'minSizePx'].includes(key)) {
            element.editedSize = true;
        }
        element.set(key as keyof BaseCanvasElement, value);
        if (['maxSizePx', 'minSizePx'].includes(key) && element.minSizePx > element.maxSizePx) {
            lastEdited.value = key as 'minSizePx' | 'maxSizePx';
            if (lastEdited.value === 'minSizePx') {
                model.maxSize = pxToPt(element.minSizePx, canvasDpi.value);
                element.set('maxSizePx', Number(value))
            } else if (lastEdited.value === 'maxSizePx') {
                model.minSize = pxToPt(element.maxSizePx, canvasDpi.value);
                element.set('minSizePx', Number(value));
            }
        }
        props.canvas.requestRenderAll();
        emit('element-props-updated', element.uuid as string);

        if (element.fire) {
            element.fire('modified');
        }
    });
};

// Lives in the shapeConfig, so it's merged into each element's own config instead of set as a prop
const updateIgnoreLineBreaks = (ignoreLineBreaks: boolean) => {
    updateElement((element) => {
        const boundedText = element as BoundedTextElement;
        if (!boundedText.shapeConfig) return;

        if (props.editMode) boundedText.setEditedProp('shapeConfig');
        boundedText.setIgnoreLineBreaks(ignoreLineBreaks);
        emit('element-props-updated', boundedText.uuid as string);
        boundedText.fire?.('modified');
    });
};

const updateFontSize = (property: 'maxSizePx' | 'minSizePx', value = 0) => {
    lastEdited.value = property;

    if (!value) {
        updateElementProp(property, 0, false)
        return;
    }
    const sizePx = ptToPx(value, canvasDpi.value);
    updateElementProp(property, sizePx, false);

    if (!type.value.circularText) return;

    updateElement((element) => {
        element.set('fontSize' as keyof BaseTextElement, element.maxSizePx);
        emit('element-props-updated', element.uuid as string);
        element.fire?.('modified');
    });
    props.canvas.requestRenderAll();
}

const updateLineSpacing = () => {
    model.lineSpacing ||= 1;
    const value = model.lineSpacing / 100
    updateElementProp('lineSpacing', value);
};

const onFontSelected = async (font: ElementMapOption, option = 1) => {
    let fontMap = new Map<number, string>();
    await updateElement((element) => {
        if (props.editMode) {
            element.setEditedProp('fontsMap', 'fontPath')
        }

        if (model.isFontMapMixed) element.fontsMap.clear();

        element.fontsMap.set(option, font as string);
        fontMap = new Map(element.fontsMap);
        loadFontIntoElement(option, undefined, element);
    });
    model.isFontMapMixed = false;
    model.fontMap = fontMap;
};

const cachedLibElements = new Map<string, LibraryDetailElement[]>();
const onLoadOption = async (option: number, value?: ElementMapOption) => {
    updateElement(async (el, list) => {
        let val = value;
        if (!list || list.length < 2) {
            return loadFontIntoElement(option, val, el);
        }

        if (el.fontLibraryId) {
            const id = `${el.fontLibraryId}-${option}`;
            const libElement =
                cachedLibElements.get(id) ||
                (await getLibraryElementDetails(el.fontLibraryId as number, FONT_TYPE, undefined, '', option));
            if (!libElement.length) return;
            cachedLibElements.set(id, libElement);
            val = libElement[0].path;
        } else {
            val = el.fontsMap.get(option);
        }
        return loadFontIntoElement(option, val, el);
    });
};

const loadFontIntoElement = async (option: number, value?: ElementMapOption, textElement?: BaseTextElement) => {
    const element = textElement || props.textElement as BaseTextElement;
    if (!element) return;

    const font = (value as string) || element.fontsMap.get(option);
    if (!font) return;

    element.fontPath = font;
    element.fontOption = option
    model.fontOption = element.fontOption;

    const fontName = await fontManagerService.loadFontFromPath(font);
    element.set('fontFamily', fontName);
    element.fontName = fontName;
    props.canvas.requestRenderAll();

    emit('element-props-updated', element.uuid as string);
};

const removeFontOptionFromMap = async (position: number) => {
    let fontMap = new Map<number, string>();
    await updateElement((element) => {
        if (props.editMode) {
            element.setEditedProp('fontsMap', 'fontPath');
        }

        element.fontsMap.delete(position);
        fontMap = new Map(element.fontsMap);
        loadFontIntoElement(1, undefined, element);
    });
    model.fontMap = fontMap;
};

const removeFontLibrary = () => {
    updateElement((element) => {
        if (props.editMode) {
            element.setEditedProp('fontLibraryId')
        }
        element.fontLibraryId = undefined;
        loadFontIntoElement(1, undefined, element);
    });
    model.isFontLibraryIdMixed = false;
    model.fontLibraryId = undefined;
};

const pickFontLibrary = () => {
    model.isFontLibraryIdMixed = false;
    emit('open-font-library-selector');
};

const onFontOptionsReordered = (options: (KeyValueDto<number, ElementMapOption> | string)[], textElement?: BaseTextElement) => {
    const element = textElement || props.textElement as BaseTextElement;
    if (!element) return;

    const fontMap = new Map<number, string>()
    let newPosition = 1;

    options.forEach((item, index) => {
        const position = index + 1;
        const value = typeof item === 'string' ? item : (item.value as string);

        fontMap.set(position, value);

        if (value === element.fontPath) {
            newPosition = position;
        }
    });

    model.fontMap = new Map(fontMap);
    if (element) {
        element.fontsMap = fontMap;
        loadFontIntoElement(newPosition, undefined, element);
        return;
    }

    updateElement((el) => {
        el.fontsMap = fontMap;
        loadFontIntoElement(newPosition, undefined, el);
    });
};

const fontFileDeleted = (fontPath: string) => {
    const textElements = props.canvas
        .getObjects()
        .filter((o) => ['path-itext', 'bounded-text', 'text-transform'].includes(o.type!));

    for (const element of textElements) {
        const textElement = element as BaseTextElement;
        const fontSet = new Set([...textElement.fontsMap.values()]);

        if (!fontSet.has(fontPath)) continue;

        fontSet.delete(fontPath);

        if (!fontSet.size) {
            const defaultFont = fontManagerService.defaultFont;
            fontSet.add(defaultFont.Path);
        }

        onFontOptionsReordered([...fontSet], textElement);
    }
};

const resetForceFontSize = async () => {
    await updateElement((element) => {
        element.forceFontSize();
    });
    props.canvas.requestRenderAll();
};

const forceFontSize = async (fontSize?: number) => {
    const size = ptToPx(fontSize || 0, canvasDpi.value);
    await updateElement((element) => {
        element.forceFontSize(size);
    });
    props.canvas.requestRenderAll();
};

onMounted(() => {
    fontManagerService.initFonts();
});

provide<TextCharacterProvider>('panelUtils', {
    showLoader: (val = true) => emit('trigger-loading', val),
    fontFileDeleted
});

return { __sfc: true,FONT_TYPE, textSideOptions, verticalAlignOptions, horizontalAlignOptions, props, emit, type, lastEdited, model, disabled, canvasDpi, isBoundedText, hasTextPathDraw, hasTextShape, initModel, isFontMapMixed, updateElement, updateElementProp, updateIgnoreLineBreaks, updateFontSize, updateLineSpacing, onFontSelected, cachedLibElements, onLoadOption, loadFontIntoElement, removeFontOptionFromMap, removeFontLibrary, pickFontLibrary, onFontOptionsReordered, fontFileDeleted, resetForceFontSize, forceFontSize, ElementLibrarySelector, CInput, CCheckbox, CButton, CSelect, valueReducer }
}

})